import {
  calculatePercentage,
  formatNumber,
  type UsageNode,
} from '@xhubio-saas/core-frontend/plugin-usage'
import { Progress, Skeleton } from '@xhubio-saas/core-frontend/ui-components'
import { useTranslation } from 'react-i18next'
import { formatDay } from './billingState'

export interface QuotaMetersProps {
  /** One node per quota type — `label` is the quota type's name from the billing catalog (`getQuotaTypes`). */
  nodes: UsageNode[] | null
  isLoading: boolean
  /** When the quota renews — the end of the current period. */
  resetDate: Date
  locale: string
}

/**
 * Quota per type as a bar with „x of y <unit>", reset date and the limit sentence (plan E3).
 *
 * 🔴 The unit is the NAME of the quota type from the catalog (E4) — never a fixed „Calls": a plan that counts
 * documents must not read as API calls.
 *
 * ⚪ Not core `QuotaSection`: it repeats the name as row title AND unit, puts a fixed period badge and an emoji at
 * the limit, and has no place for the limit sentence. The pieces it is built from are reused instead
 * (`Progress` with `autoVariant`, `calculatePercentage`, `formatNumber`).
 */
export function QuotaMeters({ nodes, isLoading, resetDate, locale }: QuotaMetersProps) {
  const { t } = useTranslation()
  const reset = formatDay(resetDate, locale)

  if (isLoading && !nodes) {
    return (
      <div className="space-y-4" data-testid="billing-quota-loading">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-2 w-full" />
      </div>
    )
  }

  if (!nodes || nodes.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="billing-quota-empty">
        {t('billingPage.quota.empty')}
      </p>
    )
  }

  return (
    <div className="space-y-5" data-testid="billing-quota">
      <ul className="space-y-5">
        {nodes.map((node) => {
          const unit = node.label
          const used = formatNumber(node.count, locale)
          const limited = typeof node.limit === 'number'
          const percentage = limited ? calculatePercentage(node.count, node.limit as number) : 0
          return (
            <li key={node.pattern} className="min-w-0" data-testid="billing-quota-row">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
                <span className="min-w-0 font-medium tabular-nums [overflow-wrap:anywhere]">
                  {limited
                    ? t('billingPage.quota.used', {
                        used,
                        limit: formatNumber(node.limit as number, locale),
                        unit,
                      })
                    : t('billingPage.quota.unlimited', { used, unit })}
                </span>
                {limited && (
                  <span
                    className={`tabular-nums ${percentage >= 100 ? 'font-medium text-destructive' : 'text-muted-foreground'}`}
                  >
                    {percentage} %
                  </span>
                )}
              </div>
              {limited && (
                <Progress
                  value={Math.min(percentage, 100)}
                  autoVariant
                  // Track darker than the primitive's `bg-secondary`, which vanishes on the white card.
                  className="mt-2 h-2 bg-muted-foreground/20"
                  aria-label={`${unit}: ${percentage} %`}
                />
              )}
            </li>
          )
        })}
      </ul>
      <p className="text-sm text-muted-foreground" data-testid="billing-quota-reset">
        {t('billingPage.quota.reset', { date: reset })}
        {/* The limit sentence only where a limit exists — an unlimited quota rejects nothing. */}
        {nodes.some((n) => typeof n.limit === 'number') &&
          ` ${t('billingPage.quota.limit', { reset })}`}
      </p>
    </div>
  )
}
